import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "./ui/sheet";

export default function Navbar() {
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  // Close mobile menu when route changes
  useEffect(() => {
    setOpen(false);
  }, [loc.pathname, loc.hash]);

  if (loc.pathname.startsWith("/admin")) return null;

  const linkCls = ({ isActive }) =>
    `text-sm tracking-wide transition-colors ${
      isActive ? "text-[#06B6D4]" : "text-[#94A3B8] hover:text-[#F8FAFC]"
    }`;

  const mobileLinkCls = ({ isActive }) =>
    `block w-full py-3 px-2 text-base tracking-wide border-b border-white/5 transition-colors ${
      isActive ? "text-[#06B6D4]" : "text-[#F8FAFC] hover:text-[#06B6D4]"
    }`;

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-40 backdrop-blur-xl bg-[#0A1628]/70 border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2.5 group">
          <img
            src="/genquotes-logo.png?v=3"
            alt="GenQuotes"
            className="h-10 sm:h-11 w-auto"
            style={{ maxHeight: 44 }}
          />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/" end className={linkCls} data-testid="nav-home">Home</NavLink>
          <NavLink to="/pricing" className={linkCls} data-testid="nav-pricing">Pricing</NavLink>
          <a
            href="/#how-it-works"
            data-testid="nav-how"
            className="text-sm tracking-wide text-[#94A3B8] hover:text-[#F8FAFC]"
          >
            How it works
          </a>
          <NavLink to="/blog" className={linkCls} data-testid="nav-blog">Blog</NavLink>
          <a
            href="/#free-consultation"
            data-testid="nav-ask-ai"
            className="text-sm tracking-wide text-[#94A3B8] hover:text-[#F8FAFC]"
          >
            Ask about AI
          </a>
          <NavLink to="/account" className={linkCls} data-testid="nav-account">Account</NavLink>
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/account"
            data-testid="nav-signin-link"
            className="hidden sm:inline-flex text-sm text-[#94A3B8] hover:text-[#F8FAFC] px-3 py-2"
          >
            Sign in
          </Link>
          <Link
            to="/get-started"
            data-testid="nav-build-widget-btn"
            className="btn-pink inline-flex items-center rounded-full px-4 py-2 text-sm font-medium"
          >
            Build my widget
          </Link>

          {/* Mobile hamburger */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                aria-label="Open menu"
                data-testid="mobile-menu-toggle"
                className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-white/10 text-[#F8FAFC] hover:bg-white/5 transition-colors"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              data-testid="mobile-menu-panel"
              className="w-[85%] sm:max-w-sm bg-[#0A1628] border-l border-white/5 text-[#F8FAFC] p-0 [&>button]:hidden"
            >
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
                <Link to="/" onClick={() => setOpen(false)} data-testid="mobile-brand-link">
                  <img
                    src="/genquotes-logo.png?v=3"
                    alt="GenQuotes"
                    className="h-9 w-auto"
                  />
                </Link>
                <button
                  type="button"
                  aria-label="Close menu"
                  data-testid="mobile-menu-close"
                  onClick={() => setOpen(false)}
                  className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-white/10 text-[#F8FAFC] hover:bg-white/5"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <nav className="px-6 py-4 flex flex-col">
                <NavLink to="/" end className={mobileLinkCls} data-testid="mobile-nav-home">
                  Home
                </NavLink>
                <NavLink to="/pricing" className={mobileLinkCls} data-testid="mobile-nav-pricing">
                  Pricing
                </NavLink>
                <a
                  href="/#how-it-works"
                  className="block w-full py-3 px-2 text-base tracking-wide border-b border-white/5 text-[#F8FAFC] hover:text-[#06B6D4]"
                  data-testid="mobile-nav-how"
                  onClick={() => setOpen(false)}
                >
                  How it works
                </a>
                <NavLink
                  to="/blog"
                  className={mobileLinkCls}
                  data-testid="mobile-nav-blog"
                  onClick={() => setOpen(false)}
                >
                  Blog
                </NavLink>
                <a
                  href="/#free-consultation"
                  className="block w-full py-3 px-2 text-base tracking-wide border-b border-white/5 text-[#F8FAFC] hover:text-[#06B6D4]"
                  data-testid="mobile-nav-ask-ai"
                  onClick={() => setOpen(false)}
                >
                  Ask about AI
                </a>
                <a
                  href="/#faq"
                  className="block w-full py-3 px-2 text-base tracking-wide border-b border-white/5 text-[#F8FAFC] hover:text-[#06B6D4]"
                  data-testid="mobile-nav-faq"
                  onClick={() => setOpen(false)}
                >
                  FAQ
                </a>
                <NavLink to="/setup" className={mobileLinkCls} data-testid="mobile-nav-setup">
                  Setup guide
                </NavLink>
                <NavLink to="/account" className={mobileLinkCls} data-testid="mobile-nav-account">
                  Account
                </NavLink>
                <NavLink to="/terms" className={mobileLinkCls} data-testid="mobile-nav-terms">
                  Terms
                </NavLink>
                <NavLink to="/privacy" className={mobileLinkCls} data-testid="mobile-nav-privacy">
                  Privacy
                </NavLink>
              </nav>

              <div className="px-6 py-5 mt-2 border-t border-white/5">
                <Link
                  to="/get-started"
                  data-testid="mobile-nav-build-widget-btn"
                  onClick={() => setOpen(false)}
                  className="btn-pink inline-flex w-full items-center justify-center rounded-full px-4 py-3 text-sm font-medium"
                >
                  Build my widget
                </Link>
                <Link
                  to="/account"
                  data-testid="mobile-nav-signin-link"
                  onClick={() => setOpen(false)}
                  className="mt-3 inline-flex w-full items-center justify-center rounded-full px-4 py-3 text-sm text-[#94A3B8] hover:text-[#F8FAFC] border border-white/10"
                >
                  Sign in
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
